import React, { forwardRef, useMemo } from "react";
import classNames from "classnames";
import StickyWithOffset from "../layout/StickyWithOffset";
import { motion, AnimatePresence } from "framer-motion";
import { colors } from "../itinerary/ItineraryDay";
import ItineraryIcon from "../stop-finder/ItineraryIcon";
import Fade from "../animations/Fade";

const motionTransition = {
    duration: 0.2,
    ease: "linear",
};

const motionExit = { translateY: "-100%" };
const motionInitial = { translateY: "100%" };
const motionAnimate = { translateY: "0%" };

const StickyGroupHeaderChild = forwardRef(function StickyGroupHeaderChild(
    {
        iconColor,
        iconName,
        classes,
        isSticky,
        style,
        bgColor,
        children,
        currentPlaceName,
        color,
        iconBorderColor,
        setIsOpenItinerarySwitcher,
        showItinerarySwitcher,
        inLauncherModal,
    },
    ref,
) {
    return useMemo(
        () => {
            const closeIframeModal = () => {
                if (!window.parent) {
                    return;
                }
                window.parent.postMessage("CLOSE_MODAL", "*");
            };
            return (
                <div
                    ref={ref}
                    className={`${classes} ${isSticky ? "is-sticky" : ""}`}
                    style={{
                        ...style,
                        backgroundColor: bgColor,
                        color,
                        borderTopColor: isSticky && bgColor !== colors.white ? bgColor : null,
                        borderBottomColor: bgColor === colors.purpleLight ? bgColor : null,
                    }}
                >
                    <div className="trpr__group-header-sticky__left">
                        <div className="trpr__group-header-sticky__left-icon">
                            <AnimatePresence exitBeforeEnter initial={true}>
                                {iconName === "moonrise" && (
                                    <motion.div
                                        key="moonrise"
                                        exit={motionExit}
                                        initial={motionInitial}
                                        animate={motionAnimate}
                                        transition={motionTransition}
                                    >
                                        <i className="trpr__icon-moonrise" style={{ color: iconColor }} />
                                    </motion.div>
                                )}
                                {iconName === "sunrise" && (
                                    <motion.div
                                        key="sunrise"
                                        exit={motionExit}
                                        initial={motionInitial}
                                        animate={motionAnimate}
                                        transition={motionTransition}
                                    >
                                        <i className="trpr__icon-sunrise" style={{ color: iconColor }} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <span className="trpr__group-header-sticky__left-text">{children}</span>
                    </div>
                    <AnimatePresence>
                        {(inLauncherModal || showItinerarySwitcher) && isSticky && (
                            <Fade className="trpr__group-header-sticky__right">
                                {showItinerarySwitcher && (
                                    <div>
                                        <button
                                            onClick={() => setIsOpenItinerarySwitcher(true)}
                                            type="button"
                                            className="trpr-itinerary-switcher-popup__header-close"
                                        >
                                            <ItineraryIcon />
                                        </button>
                                        {inLauncherModal && (
                                            <span
                                                className="trpr__group-header-sticky__right-divider"
                                                style={{ backgroundColor: iconBorderColor }}
                                            />
                                        )}
                                    </div>
                                )}
                                {inLauncherModal && (
                                    <div>
                                        <button
                                            onClick={closeIframeModal}
                                            type="button"
                                            className="trpr-itinerary-switcher-popup__header-close"
                                        >
                                            <i className="trpr__icon-close"></i>
                                        </button>
                                    </div>
                                )}
                            </Fade>
                        )}
                    </AnimatePresence>
                </div>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            color,
            iconName,
            classes,
            isSticky,
            style.left,
            style.top,
            bgColor,
            children,
            currentPlaceName,
            iconBorderColor,
            setIsOpenItinerarySwitcher,
            showItinerarySwitcher,
            inLauncherModal,
        ],
    );
});

StickyGroupHeaderChild.displayName = "StickyGroupHeaderChild";

function StickyGroupHeader(props) {
    let {
        children,
        className,
        topOffset,
        currentPlaceName,
        bgColor,
        color,
        iconName,
        iconColor,
        iconBorderColor,
        setIsOpenItinerarySwitcher,
        showItinerarySwitcher,
        inLauncherModal,
        withSticky,
        isStickyOverride,
    } = props;

    const classes = classNames(className, "trpr__group-header-sticky");

    if (!withSticky) {
        const _props = {
            setIsOpenItinerarySwitcher,
            showItinerarySwitcher,
            inLauncherModal,
            iconBorderColor,
            iconName,
            color,
            classes,
            isSticky: isStickyOverride,
            style: {},
            bgColor,
            iconColor,
            children,
            currentPlaceName,
        };
        return <StickyGroupHeaderChild {..._props}>{children}</StickyGroupHeaderChild>;
    }

    return (
        <StickyWithOffset
            topOffset={topOffset}
            render={({ style, isSticky, ref }) => {
                const _props = {
                    setIsOpenItinerarySwitcher,
                    showItinerarySwitcher,
                    inLauncherModal,
                    iconBorderColor,
                    iconName,
                    color,
                    classes,
                    isSticky,
                    style,
                    bgColor,
                    iconColor,
                    children,
                    currentPlaceName,
                    ref,
                };
                return <StickyGroupHeaderChild {..._props}>{children}</StickyGroupHeaderChild>;
            }}
        />
    );
}

StickyGroupHeader.defaultProps = {
    topOffset: 0,
};

export default StickyGroupHeader;
